import React, { FC } from 'react'
import { Form, Button, Typography, Row, Col } from 'antd'
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons'
import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { SortableDragHandle, SortableRow } from '../../../../components/sortable-row'
import UniversalColorPicker from '../../../../components/universal-color-picker'
import type { PaletteColor, UniversalColorValue } from '../../../../types/pantone-color'
import technicalColors from '../../../../data/technical-colors.json'

const MAX_COLORS = 12

type ColorListItem = {
    name: string
    code: string
}

const palette = technicalColors as PaletteColor[]

/** Дефолт при завершении отрисовки бланка: CMYK + 2 лака из техпалитры. */
const DEFAULT_COLOR_NAMES = ['Cyan', 'Magenta', 'Yellow', 'Black', 'Lak gloss', 'Lak mat.'] as const

const DEFAULT_COLORS: ColorListItem[] = DEFAULT_COLOR_NAMES.map(name => {
    const color = palette.find(c => c.name === name)
    if (!color) throw new Error(`Tech color not found: ${name}`)
    return { name: color.name, code: color.hex }
})

const ColorListItemPicker: FC<{
    value?: ColorListItem
    onChange?: (value: ColorListItem) => void
}> = ({ value, onChange }) => {
    const isTechColor = Boolean(value?.name && palette.some(c => c.name === value.name))

    const pickerValue =
        value && (value.name || value.code)
            ? {
                  name: value.name ?? '',
                  hex: value.code ?? '',
                  ...(isTechColor ? { mode: 'color' as const } : {}),
              }
            : undefined

    const handleChange = (next: UniversalColorValue) => {
        onChange?.({ name: next.name, code: next.hex })
    }

    return <UniversalColorPicker value={pickerValue} onChange={handleChange} />
}

const ColorList: React.FC = () => {
    return (
        <>
            <Typography.Title level={5}>Цвета</Typography.Title>
            <Form.List name='colors' initialValue={DEFAULT_COLORS}>
                {(fields, { add, remove, move }) => {
                    const itemIds = fields.map(field => String(field.key))

                    const onDragEnd = ({ active, over }: DragEndEvent) => {
                        if (!over || active.id === over.id) return

                        const oldIndex = itemIds.indexOf(String(active.id))
                        const newIndex = itemIds.indexOf(String(over.id))
                        if (oldIndex < 0 || newIndex < 0) return

                        move(oldIndex, newIndex)
                    }

                    return (
                        <>
                            <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
                                <SortableContext
                                    items={itemIds}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {fields.map(({ key, name, ...restField }) => (
                                        <SortableRow key={key} id={String(key)}>
                                            <Row align='top' gutter={8}>
                                                <Col>
                                                    <SortableDragHandle
                                                        style={{ marginTop: 4 }}
                                                    />
                                                </Col>
                                                <Col flex='auto'>
                                                    <Form.Item
                                                        {...restField}
                                                        name={name}
                                                        rules={[
                                                            {
                                                                validator: async (
                                                                    _,
                                                                    v: ColorListItem
                                                                ) => {
                                                                    if (
                                                                        !v?.name?.trim() ||
                                                                        !v?.code
                                                                    ) {
                                                                        return Promise.reject(
                                                                            new Error(
                                                                                'Выберите цвет и укажите название'
                                                                            )
                                                                        )
                                                                    }
                                                                },
                                                            },
                                                        ]}
                                                    >
                                                        <ColorListItemPicker />
                                                    </Form.Item>
                                                </Col>
                                                <Col>
                                                    <MinusCircleOutlined
                                                        onClick={() => remove(name)}
                                                        style={{ marginTop: 8 }}
                                                    />
                                                </Col>
                                            </Row>
                                        </SortableRow>
                                    ))}
                                </SortableContext>
                            </DndContext>

                            {fields.length < MAX_COLORS && (
                                <Form.Item>
                                    <Button
                                        type='dashed'
                                        onClick={() => add({ name: '', code: '' })}
                                        icon={<PlusOutlined />}
                                    >
                                        Добавить цвет
                                    </Button>
                                </Form.Item>
                            )}
                        </>
                    )
                }}
            </Form.List>
        </>
    )
}

export default ColorList
